import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { ButtonSpinner } from '../components/ui/Spinner';
import Input from '../components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

function LoginPage() {
    const { theme, toggleTheme } = useTheme();
    const { isAuthenticated, login } = useAuth();
    const [form, setForm] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await login(form.username, form.password);
            // Do NOT navigate here!
        } catch (err) {
            console.error('Login error:', err);
            if (err.response?.data?.detail) {
                setError(err.response.data.detail);
            } else if (err.message === 'Invalid token payload' || err.message === 'Invalid server response') {
                setError('Server error. Please try again later.');
            } else {
                setError('Invalid username or password.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <button
                onClick={toggleTheme}
                className="absolute top-4 right-4 p-2 rounded-lg text-gray-500 dark:text-gray-400 
                         hover:bg-gray-100 dark:hover:bg-gray-800 
                         focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
                {theme === 'dark' ? (
                    <SunIcon className="h-6 w-6" />
                ) : (
                    <MoonIcon className="h-6 w-6" />
                )}
            </button>

            <div className="flex flex-col items-center">
                <img
                    src={theme === 'light' ? '/pokerium_logo_light.png' : '/pokerium_logo.webp'}
                    alt="Pokerium Logo"
                    className="h-[90px] mb-6"
                />

                <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm 
                              border border-gray-200 dark:border-gray-700 
                              min-w-[340px]">
                    <h1 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                        Login
                    </h1>

                    <form onSubmit={handleSubmit}>
                        <Input
                            label="Username"
                            name="username"
                            value={form.username}
                            onChange={handleChange}
                            required
                        />

                        <Input
                            label="Password"
                            name="password"
                            type="password"
                            value={form.password}
                            onChange={handleChange}
                            required
                        />

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-primary-600 text-white rounded-lg font-bold
                                     hover:bg-primary-700 focus:outline-none focus:ring-2
                                     focus:ring-primary-500 focus:ring-offset-2
                                     disabled:opacity-50 disabled:cursor-not-allowed
                                     transition-colors duration-200"
                        >
                            {loading ? <ButtonSpinner /> : 'Login'}
                        </button>
                    </form>

                    {error && (
                        <div className="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 
                                      text-red-700 dark:text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                        Don't have an account?{' '}
                        <RouterLink 
                            to="/register" 
                            className="text-primary-600 dark:text-primary-400 font-semibold 
                                     hover:underline"
                        >
                            Register
                        </RouterLink>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginPage; 